const MAX_INPUT_IMAGE_DIMENSION = 5000;
const TARGET_UPLOAD_WIDTH = 512;
const TARGET_UPLOAD_QUALITY = 0.6;
const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'webp',
  'avif',
  'gif',
  'bmp',
  'tif',
  'tiff',
  'heic',
  'heif',
]);

const readImageFromObjectUrl = (objectUrl) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not read this image file.'));
    image.src = objectUrl;
  });

const getFileExtension = (fileName) => {
  if (!fileName || typeof fileName !== 'string') {
    return '';
  }

  const trimmedName = fileName.trim().toLowerCase();
  const lastDotIndex = trimmedName.lastIndexOf('.');
  if (lastDotIndex < 0 || lastDotIndex === trimmedName.length - 1) {
    return '';
  }

  return trimmedName.slice(lastDotIndex + 1);
};

const isSupportedImageFile = (file) => {
  if (!(file instanceof File)) {
    return false;
  }

  const mimeType = String(file.type || '').toLowerCase();
  if (mimeType.startsWith('image/')) {
    return true;
  }

  return IMAGE_EXTENSIONS.has(getFileExtension(file.name));
};

const loadImageSource = async (file, objectUrl) => {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        width: bitmap.width,
        height: bitmap.height,
        source: bitmap,
        dispose: () => {
          if (typeof bitmap.close === 'function') {
            bitmap.close();
          }
        },
      };
    } catch (error) {
      // Fall back to HTMLImageElement for browsers that reject createImageBitmap
      // on camera-native formats like HEIC/HEIF.
    }
  }

  const image = await readImageFromObjectUrl(objectUrl);
  return {
    width: image.naturalWidth || image.width,
    height: image.naturalHeight || image.height,
    source: image,
    dispose: () => {},
  };
};

const getFileBaseName = (fileName) => {
  if (!fileName || typeof fileName !== 'string') {
    return 'photo';
  }

  const trimmedName = fileName.trim();
  const lastDotIndex = trimmedName.lastIndexOf('.');
  if (lastDotIndex <= 0) {
    return trimmedName || 'photo';
  }

  return trimmedName.slice(0, lastDotIndex) || 'photo';
};

export async function normalizeUploadImageFile(
  file,
  {
    maxDimension = MAX_INPUT_IMAGE_DIMENSION,
    targetWidth = TARGET_UPLOAD_WIDTH,
    quality = TARGET_UPLOAD_QUALITY,
  } = {}
) {
  if (!isSupportedImageFile(file)) {
    throw new Error('Invalid image file.');
  }

  const objectUrl = URL.createObjectURL(file);
  let loadedImage = null;

  try {
    loadedImage = await loadImageSource(file, objectUrl);
    const sourceWidth = loadedImage.width;
    const sourceHeight = loadedImage.height;

    if (!sourceWidth || !sourceHeight) {
      throw new Error('Could not read this image file.');
    }

    const sourceMaxDimension = Math.max(sourceWidth, sourceHeight);
    const resizeCapScale = sourceMaxDimension > maxDimension
      ? maxDimension / sourceMaxDimension
      : 1;

    const cappedWidth = Math.max(1, Math.round(sourceWidth * resizeCapScale));
    const cappedHeight = Math.max(1, Math.round(sourceHeight * resizeCapScale));

    const outputWidth = Math.min(cappedWidth, targetWidth);
    const outputHeight = Math.max(1, Math.round((cappedHeight / cappedWidth) * outputWidth));

    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const context = canvas.getContext('2d', { alpha: false });
    if (!context) {
      throw new Error('Could not prepare this image for upload.');
    }

    context.drawImage(loadedImage.source, 0, 0, outputWidth, outputHeight);

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (resultBlob) => {
          if (!resultBlob) {
            reject(new Error('Could not compress this image for upload.'));
            return;
          }
          resolve(resultBlob);
        },
        'image/jpeg',
        quality
      );
    });

    return new File([blob], `${getFileBaseName(file.name)}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(
        error.message || 'Could not process this image for upload.'
      );
    }
    throw new Error('Could not process this image for upload.');
  } finally {
    if (loadedImage?.dispose) {
      loadedImage.dispose();
    }
    URL.revokeObjectURL(objectUrl);
  }
}

export const uploadImageNormalizationConfig = {
  maxDimension: MAX_INPUT_IMAGE_DIMENSION,
  targetWidth: TARGET_UPLOAD_WIDTH,
  quality: TARGET_UPLOAD_QUALITY,
};

export { isSupportedImageFile };
